import { idVideoDinUrl } from './youtube-player';

/**
 * De unde vine filmuletul pasului 3 din inscriere — partea PURA.
 *
 * Pana la b21, URL-ul era un DEFAULT SCRIS IN COD (`VideoIntroPage`, prop
 * `urlVideo` cu valoare implicita): filmuletul de test dat de owner in august.
 * Din b21 vine de la server, prin `GET /api/editions/current/onboarding-video`.
 *
 * 🔴 REGULA CARE DA FORMA FISIERULUI: filmuletul de test NU mai e rezerva.
 * Un fallback ar arata pe ecran exact ca un succes — acelasi player, acelasi
 * buton, aceeasi poarta care se deschide — dar cu ALT material decat cel al
 * clientului. Un ecran care minte plauzibil e mai rau decat unul care spune
 * „acum nu se poate". De-aia `alegeSursaVideo` are trei iesiri si niciuna nu
 * intoarce un URL pe care nu l-a dat serverul.
 *
 * ⚠️ Contractul, citit din `types.gen.ts`, nu presupus:
 *
 *     200 → OnboardingVideoDto = { bunVenitUrl?: string; litiuUrl?: string }
 *
 * **Amandoua campurile sunt OPTIONALE.** Deci un 200 poate sosi fara `litiuUrl`,
 * si asta nu e o eroare de retea: e un raspuns valid care nu contine niciun
 * film. Tipurile nu apara de cazul asta — verificarea e la RULARE, aici.
 *
 * `bunVenitUrl` exista in DTO si e IGNORAT deliberat: e alt material, al altui
 * ecran. Se testeaza explicit, ca sa nu ajunga cineva sa-l foloseasca drept
 * rezerva pentru `litiuUrl` — ar fi acelasi fel de minciuna ca filmuletul de test.
 */

/** Ce a raspuns serverul, tradus in ce arata ecranul. */
export type SursaVideo =
    | { stare: 'se-incarca' }
    | { stare: 'gata'; url: string }
    /** Eroare de retea, 200 fara film, sau un URL din care nu iese un id YouTube. */
    | { stare: 'indisponibil' };

/** Ce stie apelantul despre cerere. Numele sunt cele din `useQuery` (v5). */
export interface StareCerere {
    data: { bunVenitUrl?: string; litiuUrl?: string } | undefined;
    isError: boolean;
    isPending: boolean;
}

/**
 * Cele trei stari ale ecranului, dintr-o singura functie.
 *
 * Ordinea conteaza si e pinuita de teste: **eroarea bate asteptarea**. In
 * `useQuery` v5 cele doua nu pot fi adevarate simultan, dar functia nu are de
 * unde sti asta — un apelant care trece amandoua adevarate trebuie sa primeasca
 * un raspuns determinist, nu unul care depinde de ordinea in care am scris `if`-urile.
 *
 * `gata` cere DOUA lucruri, nu unul: un raspuns, si un URL din care
 * `idVideoDinUrl` chiar scoate un id. Fara a doua conditie, un URL de Vimeo pus
 * din greseala in configurarea backendului ar duce la un player care asteapta la
 * infinit, cu poarta inchisa si fara niciun mesaj — exact fundul de sac pe care
 * caseta de eroare a fost scrisa sa-l previna.
 */
export function alegeSursaVideo({ data, isError, isPending }: StareCerere): SursaVideo {
    if (isError) return { stare: 'indisponibil' };
    if (isPending) return { stare: 'se-incarca' };

    const url = String(data?.litiuUrl ?? '').trim();
    if (!url) return { stare: 'indisponibil' };
    if (idVideoDinUrl(url) === null) return { stare: 'indisponibil' };

    return { stare: 'gata', url };
}
